import React from "react";
import { useNavigate } from "react-router-dom";
import "./PackageBuyPromptModal.css";

/**
 * "Buy package" — the step BETWEEN clicking a package and paying for it.
 *
 * Why this exists: on the home page a featured package card used to navigate straight to the global
 * packages listing, under a button that read "View Packages". That was never the intent — there was
 * simply no per-package customer route to send anyone to, and `HomePage.js` said so in a comment.
 * Phase 15 built that route, so the card can now do the thing it always claimed to: open the package,
 * say what it costs, and take the buyer to its own purchase page.
 *
 * WHAT THIS IS NOT: it is not a purchase. It collects nothing, validates nothing and calls no checkout
 * route. It states what is about to be bought and hands over to the Phase 15 page, so there is still
 * exactly one place where a package is purchased. A second purchase implementation is the defect that
 * Phases 2 and 3 were both caused by.
 *
 * It also keeps the user's distinction between the two intents on the home page:
 *   - a package CARD is "I want THIS package"   -> this prompt -> its purchase page
 *   - "View All Packages" is "show me the lot"  -> /packages, unchanged
 */
const PackageBuyPromptModal = ({ pkg, onClose }) => {
  const navigate = useNavigate();

  if (!pkg) return null;

  // `/featured` returns `availableAtShops` (populated ShopLocation documents), not `shopId`. A
  // package can be sold at more than one location; the purchase page needs a location to price the
  // platform fee against, so the first one is passed exactly as the card's own data provides it.
  const firstLocation = pkg.availableAtShops?.[0];
  const shopId =
    typeof firstLocation === "string" ? firstLocation : firstLocation?._id;

  const price = Number(pkg.packagePrice) || 0;
  const regularPrice = Number(pkg.regularPrice) || 0;
  const saving = regularPrice > price ? regularPrice - price : 0;

  const services = (pkg.services || [])
    .map((service) => service.serviceId?.name || service.serviceName)
    .filter(Boolean);

  const goToPurchasePage = () => {
    const query = shopId ? `?shopId=${encodeURIComponent(shopId)}` : "";
    navigate(`/packages/${pkg._id}/purchase${query}`);
  };

  return (
    <div
      className="pbp-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Buy ${pkg.name}`}
    >
      <div className="pbp-card" onClick={(e) => e.stopPropagation()}>
        <button
          className="pbp-close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          ✕
        </button>

        <h3 className="pbp-title">{pkg.name}</h3>

        <div className="pbp-price-row">
          <span className="pbp-price">${price.toFixed(2)}</span>
          {saving > 0 && (
            <>
              <span className="pbp-price-was">${regularPrice.toFixed(2)}</span>
              <span className="pbp-save">Save ${saving.toFixed(2)}</span>
            </>
          )}
        </div>

        {pkg.totalSessions ? (
          <div className="pbp-sessions">
            {pkg.totalSessions} sessions included
          </div>
        ) : null}

        {services.length > 0 && (
          <ul className="pbp-services">
            {services.map((name, index) => (
              <li key={`${name}-${index}`}>✓ {name}</li>
            ))}
          </ul>
        )}

        {/* Says what the next step is, because the next step is a page rather than a dialog. */}
        <p className="pbp-note">
          You will choose your details and pay on the package's own page. No
          account is needed.
        </p>

        <div className="pbp-actions">
          <button
            type="button"
            className="pbp-buy"
            onClick={goToPurchasePage}
          >
            Buy package
          </button>
          <button type="button" className="pbp-cancel" onClick={onClose}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageBuyPromptModal;
